import { useNavigation, useTranslate } from "@refinedev/core";
import { Button, Dropdown, Flex, Space, Typography, Divider } from "antd";
import { DeleteButton, DateField } from "@refinedev/antd";
import { useAntTheme } from "hooks/useAntTheme";
import {
  BugDroidIcon,
  BuildingsIcon,
  CopySimpleIcon,
  DotsThreeVerticalIcon,
  FilePdfIcon,
  NotePencilIcon,
  TrashSimpleIcon,
  DownloadSimpleIcon,
  BracketsCurlyIcon,
  UsersIcon,
  GearIcon,
  BrowserIcon,
} from "@phosphor-icons/react";
import { useOrganization } from "hooks/useOrganization";
import { useNavigate } from "react-router-dom";
import { MediaProjectResponse } from "pages/media/types";
import { useWorkspace } from "hooks/useWorkspace";
import { useClipboard } from "hooks/useClipboard";
import { Trans } from "react-i18next";
import { ReactNode } from "react";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import useIsAdmin from "hooks/useIsAdmin";
import { useProjectPublications } from "../hooks/useProjectPublications";

const PrimaryText = ({ children }: { children?: ReactNode }) => {
  const { theme } = useAntTheme();
  return (
    <Typography.Text style={{ color: theme.colorText }}>
      {children}
    </Typography.Text>
  );
};
export const ProjectSecondaryMenu = ({
  project,
  storyboardId,
  instanceId,
}: {
  project: MediaProjectResponse;
  storyboardId: string;
  instanceId: string;
}) => {
  const { theme } = useAntTheme();
  const t = useTranslate();
  const { list } = useNavigation();
  const { getOrganization } = useOrganization({});
  const { getWorkspace } = useWorkspace({
    organizationId: project?.organization_id,
  });
  const navigate = useNavigate();
  const { copy } = useClipboard();
  const { trackEvent } = useAnalytics();
  const hasAccessToDebugMedia = useIsAdmin();
  const { isInstanceLive } = useProjectPublications(project?.id);
  const editingMedia = project?.media.find(
    (media) => !isInstanceLive(media.instance_id)
  );
  const media = project?.media?.[0];

  const organization = getOrganization(project?.organization_id);
  const workspace = getWorkspace(project?.workspace_id);
  const storyboard = project?.storyboards.find(
    (storyboard) => storyboard.id === storyboardId
  );
  const primaryDocument = project?.documents.find(
    (document) => document.id === project?.storyboards[0]?.primary_document_id
  );

  const isDocumentTypeWebPage = primaryDocument?.document_type === "WebPage";
  const webPageUrl = primaryDocument?.source_url ?? "";

  const handleDeleteSuccess = () => {
    trackEvent(AnalyticsEvents.ProjectDelete, {
      project_id: project?.id,
    });
    list("media_projects");
  };

  const getDocumentMenuItem = () => {
    if (!primaryDocument) return null;

    const isWebPage = isDocumentTypeWebPage;

    return {
      key: isWebPage ? "webPageUrl" : "document",
      disabled: false,
      label: (
        <Flex vertical>
          <Typography.Text type="secondary">
            <Flex align="center" gap={5}>
              {isWebPage ? (
                <BrowserIcon size={20} />
              ) : (
                <FilePdfIcon size={20} />
              )}
              {t(
                `media.projects.projectMenu.${isWebPage ? "fromPage" : "fromDocument"}`
              )}
            </Flex>
          </Typography.Text>
          {isWebPage ? (
            <Typography.Link
              href={webPageUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                maxWidth: 300,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={webPageUrl}
            >
              {webPageUrl}
            </Typography.Link>
          ) : (
            <Typography.Text>{primaryDocument?.name}</Typography.Text>
          )}
        </Flex>
      ),
    };
  };

  const menuItems = [
    {
      key: "edit-storyboard",
      disabled: ["Pending", "Generating"].includes(storyboard?.status),
      label: (
        <Button
          key="edit-storyboard"
          type="text"
          size="middle"
          style={{
            background: "none",
            paddingLeft: 0,
          }}
          icon={<NotePencilIcon size={20} />}
        >
          {t("media.projects.projectMenu.editStoryboard")}
        </Button>
      ),
      onClick: () => {
        navigate(
          `/media/projects/${project.id}/storyboard/${storyboard?.asset_id}`
        );
      },
    },
    {
      key: "edit-video",
      disabled: !isInstanceLive(media?.instance_id),
      label: (
        <Button
          key="edit-video"
          type="text"
          size="middle"
          style={{
            background: "none",
            paddingLeft: 0,
          }}
          icon={<NotePencilIcon size={20} />}
        >
          {t("media.projects.projectMenu.editVideo")}
        </Button>
      ),
      onClick: () => {
        navigate(
          `/media/projects/${project.id}/storyboard/${storyboardId}/templates`
        );
      },
    },
    {
      key: "edit-document-animation",
      disabled: ["Draft", "Pending", "Generating"].includes(
        project?.media?.[0]?.status
      ),
      label: (
        <Button
          key="edit-document-animation"
          type="text"
          size="middle"
          style={{
            background: "none",
            paddingLeft: 0,
          }}
          icon={<NotePencilIcon size={20} />}
        >
          {t("media.projects.projectMenu.documentAnimation")}
        </Button>
      ),
      onClick: () => {
        navigate(
          `/media/projects/${project.id}/media/${editingMedia?.id}/animation`
        );
      },
    },
    {
      key: "edit-kb",
      disabled: false,
      label: (
        <Button
          key="edit-kb"
          type="text"
          size="middle"
          style={{
            background: "none",
            paddingLeft: 0,
          }}
          icon={<NotePencilIcon size={20} />}
        >
          {t("media.projects.projectMenu.editKbChatbot")}
        </Button>
      ),
      onClick: () => {
        navigate(`/media/projects/${project.id}/kb`);
      },
    },
    {
      key: "download",
      disabled: !media?.url,
      label: (
        <Button
          key="download"
          type="text"
          size="middle"
          style={{
            background: "none",
            paddingLeft: 0,
          }}
          icon={<DownloadSimpleIcon size={20} />}
        >
          {t("media.projects.projectMenu.downloadVideo")}
        </Button>
      ),
      onClick: () => {
        globalThis.open(media.url, "_blank");
      },
    },
    {
      key: "divider1",
      disabled: false,
      label: (
        <div style={{ overflow: "hidden", width: "100%" }}>
          <Divider
            style={{
              margin: 0,
              left: -5,
              position: "absolute",
              width: "102%",
            }}
          />
        </div>
      ),
    },
    {
      key: "edit-project",
      disabled: false,
      label: (
        <Button
          key="edit-project"
          type="text"
          size="middle"
          style={{
            background: "none",
            paddingLeft: 0,
          }}
          icon={<GearIcon size={20} />}
        >
          {t("media.projects.projectMenu.setupProject")}
        </Button>
      ),
      onClick: () => {
        navigate(`/media/projects/${project.id}/${storyboardId}/setup`);
      },
    },
    {
      key: "copy-id",
      disabled: false,
      label: (
        <Button
          key="copy-id"
          type="text"
          size="middle"
          style={{
            background: "none",
            paddingLeft: 0,
          }}
          icon={<CopySimpleIcon size={20} />}
        >
          {t("media.projects.projectMenu.copyProjectId")}
        </Button>
      ),
      onClick: () => copy(project.id),
    },
    {
      key: "debug-project",
      disabled: !hasAccessToDebugMedia,
      label: (
        <Button
          key="debug"
          icon={<BracketsCurlyIcon size={20} />}
          type="text"
          size="middle"
          style={{ background: "none", paddingLeft: 0 }}
        >
          {t("media.projects.projectMenu.debugProject")}
        </Button>
      ),
      onClick: () => {
        navigate(`/media/projects/${project.id}/all`);
      },
    },
    {
      key: "debug-video",
      disabled: !hasAccessToDebugMedia || !media?.id,
      label: (
        <Space>
          <Button
            key="debug"
            icon={<BugDroidIcon size={20} />}
            type="text"
            size="middle"
            style={{ background: "none", paddingLeft: 0 }}
          >
            {t("media.projects.projectMenu.debugVideo")}
          </Button>
        </Space>
      ),
      onClick: () => {
        navigate(`/media/media/${media.id}`);
      },
    },
    {
      key: "delete",
      disabled: false,
      label: (
        <DeleteButton
          resource="media/projects"
          size="middle"
          recordItemId={project?.id}
          accessControl={{ enabled: false }}
          onSuccess={handleDeleteSuccess}
          icon={<TrashSimpleIcon size={20} />}
          style={{
            width: "100%",
            margin: 0,
            paddingLeft: 0,
            border: "none",
            backgroundColor: "transparent",
            boxShadow: "none",
            justifyContent: "flex-start",
          }}
        >
          {t("media.projects.detail.deleteProject")}
        </DeleteButton>
      ),
    },
    {
      key: "divider2",
      disabled: false,
      label: (
        <div style={{ overflow: "hidden" }}>
          <Divider
            style={{
              margin: 0,
              left: -5,
              position: "absolute",
              width: "102%",
            }}
          />
        </div>
      ),
    },
    {
      key: "projectCreated",
      disabled: false,
      label: (
        <Typography.Text type="secondary">
          {t("media.projects.projectMenu.createdOn")}{" "}
          <DateField
            value={project?.created}
            type="secondary"
            format="LLL"
            style={{ fontSize: "90%" }}
          />
        </Typography.Text>
      ),
    },
    getDocumentMenuItem(),
    {
      key: "organization",
      disabled: false,
      label: (
        <Typography.Text type="secondary">
          <Flex align="center" gap={5}>
            <BuildingsIcon size={20} />
            <Trans components={{ strong: <PrimaryText /> }}>
              {t("media.projects.projectMenu.inOrganization", {
                asset: organization?.name,
              })}
            </Trans>
          </Flex>
        </Typography.Text>
      ),
    },
    {
      key: "workspace",
      disabled: false,
      label: (
        <Typography.Text type="secondary">
          <Flex align="center" gap={5}>
            <UsersIcon size={20} />
            <Trans components={{ strong: <PrimaryText /> }}>
              {t("media.projects.projectMenu.inWorkspace", {
                asset: workspace?.name,
                strong: <strong />,
              })}
            </Trans>
          </Flex>
        </Typography.Text>
      ),
    },
  ];

  return (
    <Dropdown
      menu={{
        items: menuItems
          .filter(Boolean)
          .filter((item) => item.disabled === false),
      }}
      trigger={["click"]}
    >
      <DotsThreeVerticalIcon
        size={32}
        weight="bold"
        color={theme.colorPrimary}
      />
    </Dropdown>
  );
};
